import { subDays } from 'date-fns';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Suspense, useMemo, useState } from 'react';
import { type DateRange } from 'react-day-picker';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage, useIntl } from 'react-intl';

import InfoIcon from '@/assets/icons/information-circle.svg';
import {
  DateRangePicker,
  DateRangePreset,
  LIFETIME,
} from '@/components/ui/date-range-picker';
import { Tooltip } from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { useLocalStorageState } from '@/hooks/use-local-storage-state';

import { Metrics } from './metrics';
import { cn } from '@/utils/classnames';

const ANALYTICS_EXPANDED_KEY = 'source.dashboard.analyticsExpanded';
const ANALYTICS_PRESET_KEY = 'source.dashboard.analyticsPreset';

const DEFAULT_PRESET: DateRangePreset = '15';

function dateRangeFromPreset(preset: DateRangePreset): DateRange {
  if (preset === LIFETIME || preset === 'custom') {
    return { from: subDays(new Date(), 15), to: new Date() };
  }
  const now = new Date();
  return { from: subDays(now, Number(preset)), to: now };
}

export function Analytics() {
  const intl = useIntl();

  const [isExpanded, setIsExpanded] = useLocalStorageState(
    ANALYTICS_EXPANDED_KEY,
    true,
  );

  // Persist only numeric/lifetime presets. Custom date picks are ephemeral —
  // on next visit the last numeric preset is restored and dates recomputed fresh.
  const [persistedPreset, setPersistedPreset] =
    useLocalStorageState<DateRangePreset>(ANALYTICS_PRESET_KEY, DEFAULT_PRESET);

  // Local preset drives the picker UI (can be 'custom'); persisted preset
  // drives what survives a refresh (never 'custom').
  const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>(
    () => persistedPreset,
  );

  const [date, setDate] = useState<DateRange>(() =>
    dateRangeFromPreset(persistedPreset),
  );

  const fromDate = useMemo(
    () => date.from?.toISOString() ?? '',
    [date.from],
  );
  const toDate = useMemo(
    () => date.to?.toISOString() ?? '',
    [date.to],
  );

  return (
    <ErrorBoundary fallback={null}>
      <div className="shadow-base flex w-full flex-col overflow-x-auto rounded-lg bg-white">
        <button
          type="button"
          onClick={() => setIsExpanded(prev => !prev)}
          aria-expanded={isExpanded}
          className={cn(
            'hover:bg-fx-neutral-50 flex flex-row items-center gap-4 border-b border-neutral-200 px-4 py-1.5',
            isExpanded ? 'rounded-t-lg' : 'rounded-lg',
          )}
        >
          {/* Title */}
          <div className="flex items-center gap-1">
            <Typography variant="body2">
              <FormattedMessage
                defaultMessage="Portfolio metrics"
                description="Heading for a summary of recruitment performance data"
              />
            </Typography>
            <Tooltip
              content={intl.formatMessage({
                defaultMessage:
                  'Spend, hires, and efficiency across all openings for the selected period.',
                description:
                  'Explanatory note: the portfolio metrics covers spending, hires, and efficiency across job openings over a selected period',
              })}
            >
              <img src={InfoIcon} alt="Info" className="h-4 w-4" />
            </Tooltip>
          </div>

          {/* Date filter */}
          <div onClick={e => e.stopPropagation()}>
            <DateRangePicker
              selectedDate={date}
              setSelectedDate={setDate}
              selectedPreset={selectedPreset}
              onPresetChange={preset => {
                setSelectedPreset(preset);
                if (preset !== 'custom') setPersistedPreset(preset);
              }}
              showLifetimePreset={false}
            />
          </div>

          {/* Expand/collapse */}
          <span className="ml-auto">
            {isExpanded ? (
              <ChevronUp className="text-fx-primary size-4" />
            ) : (
              <ChevronDown className="text-fx-primary size-4" />
            )}
          </span>
        </button>

        {isExpanded && (
          <Suspense fallback={<Metrics.Skeleton />}>
            <Metrics fromDate={fromDate} toDate={toDate} />
          </Suspense>
        )}
      </div>
    </ErrorBoundary>
  );
}
